// Deterministic "live" sample data that changes once per day.
// Seeded by the UTC date so every visitor sees the same numbers on a given day,
// with a gentle long-term upward drift plus small daily noise. No API keys needed.
import { heroFloatingLabels } from '../mock/mock';

export { heroFloatingLabels };

const MS_PER_DAY = 86400000;
const DAY = Math.floor(Date.now() / MS_PER_DAY);
// Day index for 2026-01-01 (UTC) — used as the growth baseline.
const EPOCH_DAY = Math.floor(Date.UTC(2026, 0, 1) / MS_PER_DAY);
const growthDays = Math.max(0, DAY - EPOCH_DAY);
// Long-term drift: creeps up ~1.8%/month, capped at +75%.
const DRIFT = 1 + Math.min(growthDays * 0.0006, 0.75);

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rngFor = (offset) => mulberry32((DAY * 2654435761) ^ (offset * 40503));

// A drifted, daily-jittered value from a base.
function val(base, offset, vol = 0.05) {
  const r = rngFor(offset)();
  const noise = (r * 2 - 1) * vol;
  return base * DRIFT * (1 + noise);
}
// Believable positive daily trend, e.g. "+12.4%".
function trendPct(offset, lo, hi) {
  const r = rngFor(offset + 777)();
  return '+' + (lo + r * (hi - lo)).toFixed(1) + '%';
}
// Ascending 12-point series with slight noise (for charts).
function series(offset, start, end, vol = 0.08) {
  const out = [];
  for (let i = 0; i < 12; i++) {
    const t = i / 11;
    const baseV = start + (end - start) * t;
    const r = rngFor(offset + i)();
    out.push(Math.max(6, Math.round(baseV * (1 + (r * 2 - 1) * vol))));
  }
  return out;
}

// ===== Hero "Performance Intelligence" card =====
export const heroDashboardMetrics = [
  { label: 'Impressions', value: (val(48.2e6, 1) / 1e6).toFixed(1) + 'M', trend: trendPct(1, 6, 18) },
  { label: 'Clicks', value: (val(1.94e6, 2) / 1e6).toFixed(2) + 'M', trend: trendPct(2, 4, 14) },
  { label: 'Installs', value: Math.round(val(312e3, 3) / 1e3) + 'K', trend: trendPct(3, 8, 22) },
  { label: 'Conversions', value: (val(86.5e3, 4) / 1e3).toFixed(1) + 'K', trend: trendPct(4, 5, 15) },
  { label: 'ROAS', value: val(3.8, 5, 0.04).toFixed(1) + 'x', trend: '+' + (0.2 + rngFor(5)() * 0.7).toFixed(1) + 'x' },
  { label: 'Revenue', value: '$' + (val(1.42e6, 6) / 1e6).toFixed(2) + 'M', trend: trendPct(6, 10, 28) },
];
export const heroChart = series(20, 38, 92);

// ===== Client "Campaign Overview" dashboard =====
function channelSplit() {
  const base = [
    { label: 'OEM', b: 34 },
    { label: 'Programmatic', b: 26 },
    { label: 'CTV', b: 18 },
    { label: 'Social', b: 14 },
    { label: 'Affiliate', b: 8 },
  ];
  const raw = base.map((c, i) => ({ label: c.label, w: c.b * (1 + (rngFor(50 + i)() * 2 - 1) * 0.18) }));
  const total = raw.reduce((s, c) => s + c.w, 0);
  const pcts = raw.map((c) => ({ label: c.label, pct: Math.round((c.w / total) * 100) }));
  const diff = 100 - pcts.reduce((s, c) => s + c.pct, 0);
  pcts[0].pct += diff; // keep the sum at exactly 100
  return pcts;
}

export const dashboardDemo = {
  metrics: [
    { label: 'Spend', value: '$' + Math.round(val(125000, 11)).toLocaleString('en-US') },
    { label: 'Installs', value: Math.round(val(84500, 12)).toLocaleString('en-US') },
    { label: 'CPA', value: '$' + val(1.48, 13, 0.05).toFixed(2) },
    { label: 'ROAS', value: val(3.8, 14, 0.04).toFixed(1) + 'x' },
    { label: 'Conversion Rate', value: val(6.7, 15, 0.05).toFixed(1) + '%' },
    { label: 'Retention', value: Math.round(val(42, 16, 0.04)) + '%' },
  ],
  spendBars: series(30, 42, 100),
  revenueLine: series(40, 20, 98),
  channelSplit: channelSplit(),
};

// ===== Headline stat counters (floor at base so they never read below advertised) =====
export const performanceStats = [
  { value: Math.max(500, Math.round(val(500, 21, 0.03))), suffix: 'M+', label: 'Monthly Impressions' },
  { value: Math.max(150, Math.round(val(150, 22, 0.02))), suffix: '+', label: 'Global Markets' },
  { value: Math.max(1000, Math.round(val(1000, 23, 0.03))), suffix: '+', label: 'Active Campaigns' },
  { value: Math.max(50, Math.round(val(50, 24, 0.03))), suffix: 'M+', label: 'Users Acquired' },
  { value: Math.max(100, Math.round(val(100, 25, 0.03))), suffix: '+', label: 'Growth Partners' },
];

export const stats = [
  { value: Math.max(500, Math.round(val(500, 21, 0.03))), suffix: 'M+', label: 'Monthly Impressions' },
  { value: Math.max(150, Math.round(val(150, 22, 0.02))), suffix: '+', label: 'Global Markets' },
  { value: Math.max(1000, Math.round(val(1000, 23, 0.03))), suffix: '+', label: 'Active Campaigns' },
  { value: Math.max(100, Math.round(val(100, 25, 0.03))), suffix: '+', label: 'Growth Partners' },
];

// ===== "Every Click Has a Story" funnel (reuses hero seeds so it stays consistent) =====
const _impr = val(48.2e6, 1);
const _clicks = val(1.94e6, 2);
const _installs = val(312e3, 3);
const _purchase = val(86.5e3, 4);
const _roas = val(3.8, 5, 0.04);
const _revenue = val(1.42e6, 6);
const _reg = _installs * (0.74 + rngFor(60)() * 0.06);
const _ret = val(42, 16, 0.04);
const _cpa = val(1.48, 13, 0.05);

export const journeyStages = [
  { key: 'impression', label: 'Impression', metric: (_impr / 1e6).toFixed(1) + 'M', sub: 'Monthly reach', action: 'Premium, brand-safe inventory across OEM, programmatic & CTV.' },
  { key: 'click', label: 'Click', metric: (_clicks / 1e6).toFixed(2) + 'M', sub: (_clicks / _impr * 100).toFixed(1) + '% CTR', action: 'Creative A/B testing and placement optimization lift engagement.' },
  { key: 'install', label: 'Install', metric: Math.round(_installs / 1e3) + 'K', sub: Math.round(_installs / _clicks * 100) + '% CVR', action: 'Bid-to-install optimization with real-time fraud filtering.' },
  { key: 'registration', label: 'Registration', metric: Math.round(_reg / 1e3) + 'K', sub: Math.round(_reg / _installs * 100) + '% of installs', action: 'Onboarding-event optimization drives first-open & sign-up.' },
  { key: 'purchase', label: 'Purchase', metric: (_purchase / 1e3).toFixed(1) + 'K', sub: '$' + _cpa.toFixed(2) + ' CPA', action: 'Purchase-event bidding targets high-intent, high-LTV users.' },
  { key: 'retention', label: 'Retention', metric: Math.round(_ret) + '%', sub: 'D30 retained', action: 'Lifecycle retargeting and re-engagement keep users active.' },
  { key: 'revenue', label: 'Revenue', metric: '$' + (_revenue / 1e6).toFixed(2) + 'M', sub: _roas.toFixed(1) + 'x ROAS', action: 'LTV-based scaling compounds revenue while holding KPIs.' },
];
